import { getDay } from 'date-fns'
import { AxiosResponse } from 'axios'

import axios from 'settings/axios'
import { apiEndpoints } from 'settings/_apiSettings'
import { Reservation, ReservationRecurrence, exceptionType } from 'models/Reservation'

type RecurrencyException = {
  exception_date_utc: string
  exception_type: exceptionType
  next_available_date: string
}

interface ReservationResponse {
  reservations: {
    reservation_uuid: string
    recurrence: ReservationRecurrence
    patient_username: string
    professional_username: string
    patient_full_name: string
    patient_cellphone: string
    professional_full_name: string
    professional_cellphone: string
    professional_picture_url: string
    start_date: string
    end_date: string
    hour: string
    reservation_creation_date: string
    recurrency_exceptions: RecurrencyException[]
  }[]
}

interface GetReservations {
  username: string
  isProfessional: boolean
}

const days = {
  0: 'Domingos',
  1: 'Segundas-feiras',
  2: 'Terças-feiras',
  3: 'Quartas-feiras',
  4: 'Quintas-feiras',
  5: 'Sextas-feiras',
  6: 'Sábados'
}

const groupReservationsByWeekday = (reservations: Reservation[]) => {
  const filteredReservations = reservations.reduce((prev, curr) => {
    const reservationWeekday = days[getDay(curr.startDate)]
    
    if (prev[reservationWeekday]) {
      prev[reservationWeekday] = [...prev[reservationWeekday], curr]
    } else {
      prev[reservationWeekday] = [curr]
    }

    return prev
  },{})

  return filteredReservations
}

const removeDuplicateExceptions  = (recurrencyExceptions: RecurrencyException[]) =>
  recurrencyExceptions.filter(({ exception_date_utc, exception_type, next_available_date }, index, self) =>
    index === self.findIndex((otherException) => (
      otherException.exception_date_utc === exception_date_utc &&
      otherException.exception_type === exception_type &&
      otherException.next_available_date === next_available_date
    ))
  )

const getReservations = ({ username, isProfessional }: GetReservations, groupByWeekday = false): Promise<Reservation[] | Reservation> => {

  return new Promise((resolve, reject) => {
    axios.get(apiEndpoints.RESERVATIONS(username, isProfessional))
      .then((resp: AxiosResponse<ReservationResponse>) => {
        const { reservations } = resp.data
       
        if (reservations) {
          const data = reservations.map(reservation => ({
            uuid: reservation.reservation_uuid,
            startDate: new Date(reservation.start_date),
            creationDate: new Date(reservation.reservation_creation_date),
            patientUsername: reservation.patient_username,
            patientFullName: reservation.patient_full_name,
            patientCellphone: reservation.patient_cellphone,
            professionalUsername: reservation.professional_username,
            professionalFullName: reservation.professional_full_name,
            professionalCellphone: reservation.professional_cellphone,
            professionalPictureUrl: reservation.professional_picture_url,
            recurrence: reservation.recurrence,
            recurrencyExceptions: 
              removeDuplicateExceptions(reservation.recurrency_exceptions)
                .map(recurrencyException => ({
                  exceptionDate: recurrencyException.exception_date_utc,
                  exceptionType: recurrencyException.exception_type,
                  nextAvailableDate: recurrencyException?.next_available_date
                }))
          }))

          if (groupByWeekday) {
            resolve(groupReservationsByWeekday(data) as Reservation)
          } else {
            resolve(data)
          }
        }
      })
      .catch(e => {
        console.log(e)
        reject(e.response)
      })
  })
}

export { getReservations }